// "Push to switch", "Retire number" and the per-number switch history, used by
// NumberOwnership. The decisions live in switchPushRules.ts; this file renders them.
//
// Push writes ivr_dialing_info on a number's HOME switch so its label agrees with
// who holds it in cpass. The server decides every row (relabel / park as FREE /
// no change / blocked, never a label billing does not recognise); this modal only
// shows that decision and, in apply mode, sends it back with the preview hash.
// The confirmation is a second step inside the modal, never window.confirm.
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Check, Info, Loader2, Send, Undo2, X, Archive } from 'lucide-react';
import type { ProviderNumber } from 'api/services/providers.service';
import {
  switchPushService, SwitchPushMode, SwitchPushPreview, SwitchPushApplyResult, SwitchPushRecord,
  RetireSwitchOutcome,
} from 'api/services/switchPush.service';
import {
  PARKED_NOTE, ACTION_TEXT, actionPill, applyBlockReason, applicableCount, canRollback, currentRow,
  describeRow, retireConsequences, writes,
} from './switchPushRules';
import { formatWhen } from './numberOwnershipRules';

const Modal: React.FC<{ onClose: () => void; busy: boolean; wide?: boolean; children: React.ReactNode }> = ({
  onClose, busy, wide, children,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
       style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => !busy && onClose()}>
    <div className={`tl-card w-full ${wide ? 'max-w-5xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}
         onClick={e => e.stopPropagation()}>
      {children}
    </div>
  </div>
);

const ParkedNote: React.FC = () => (
  <div className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-1.5"
       style={{ background: 'var(--surface-raised)', color: 'var(--text-muted)' }}>
    <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
    <span>{PARKED_NOTE}</span>
  </div>
);

const ErrorLine: React.FC<{ text: string }> = ({ text }) => (
  <div className="text-sm rounded-xl px-3 py-2 flex items-start gap-2"
       style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)' }}>
    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" /> <span>{text}</span>
  </div>
);

const ResultTable: React.FC<{
  result: SwitchPushApplyResult;
  rolledBack: Record<number, string>;
  onRollback: (pushId: number) => void;
  busy: boolean;
}> = ({ result, rolledBack, onRollback, busy }) => (
  <div className="space-y-2">
    <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
      {result.ok} written · {result.failed} failed · {result.skipped} skipped
    </div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[12px]" style={{ color: 'var(--text-muted)' }}>
          <th className="px-2 py-1.5 font-medium">Number</th>
          <th className="px-2 py-1.5 font-medium">Action</th>
          <th className="px-2 py-1.5 font-medium">Status</th>
          <th className="px-2 py-1.5 font-medium">Detail</th>
          <th className="px-2 py-1.5 font-medium text-right" />
        </tr>
      </thead>
      <tbody>
        {result.results.map(r => (
          <tr key={`${r.numberId}-${r.pushId}`} className="border-t" style={{ borderColor: 'var(--border)' }}>
            <td className="px-2 py-2 font-medium text-gray-900 dark:text-white">{r.e164}</td>
            <td className="px-2 py-2"><span className={actionPill(r.action)}>{ACTION_TEXT[r.action]}</span></td>
            <td className="px-2 py-2">
              <span className={r.status === 'OK' ? 'tl-pill tl-pill-success' : r.status === 'FAILED' ? 'tl-pill tl-pill-coral' : 'tl-pill'}>
                {r.status}
              </span>
              {r.status === 'OK' && r.cacheRefreshed === false && (
                <div className="text-[11px]" style={{ color: 'var(--amber-label)' }}>cache not refreshed (≤60 s)</div>
              )}
            </td>
            <td className="px-2 py-2 text-[12px]" style={{ color: 'var(--text-muted)' }}>{r.detail}</td>
            <td className="px-2 py-2 text-right">
              {r.status === 'OK' && writes(r) && r.pushId != null && (
                rolledBack[r.pushId]
                  ? <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{rolledBack[r.pushId]}</span>
                  : (
                    <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy}
                            onClick={() => onRollback(r.pushId as number)}>
                      <Undo2 className="w-3.5 h-3.5" /> Rollback
                    </button>
                  )
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// ── Push to switch ─────────────────────────────────────────────────────────────

export const SwitchPushModal: React.FC<{
  numbers: ProviderNumber[];
  onClose: (changed: boolean) => void;
}> = ({ numbers, onClose }) => {
  const [mode, setMode] = useState<SwitchPushMode | null>(null);
  const [preview, setPreview] = useState<SwitchPushPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<SwitchPushApplyResult | null>(null);
  const [rolledBack, setRolledBack] = useState<Record<number, string>>({});

  const ids = numbers.map(n => n.id).filter((x): x is number => typeof x === 'number');
  const idKey = ids.join(',');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const list = idKey ? idKey.split(',').map(Number) : [];
    const [m, p] = await Promise.all([switchPushService.mode(), switchPushService.preview(list)]);
    if (m.success && m.data) setMode(m.data.mode);
    if (p.success && p.data) setPreview(p.data);
    else setError(p.error || 'Could not preview the push');
    setLoading(false);
  }, [idKey]);

  useEffect(() => { load(); }, [load]);

  const blockReason = applyBlockReason(mode, preview);
  const n = applicableCount(preview);

  const doApply = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    const r = await switchPushService.apply(preview.previewHash, ids);
    setBusy(false);
    setConfirming(false);
    if (r.success && r.data) setResult(r.data);
    else setError(r.error || 'The push was refused');
  };

  const doRollback = async (pushId: number) => {
    setBusy(true);
    setError(null);
    const r = await switchPushService.rollback(pushId);
    setBusy(false);
    if (r.success && r.data) {
      setRolledBack(m => ({ ...m, [pushId]: r.data!.status === 'OK' ? 'rolled back' : `rollback ${r.data!.status}` }));
      if (r.data.status !== 'OK') setError(r.data.detail || 'Rollback failed');
    } else setError(r.error || 'Rollback was refused');
  };

  return (
    <Modal onClose={() => onClose(!!result)} busy={busy} wide>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Push {numbers.length} number{numbers.length === 1 ? '' : 's'} to the switch
        </h2>
        <button onClick={() => !busy && onClose(!!result)} aria-label="Close"><X className="w-4 h-4" /></button>
      </div>
      <p className="text-[12.5px] mb-3" style={{ color: 'var(--text-muted)' }}>
        Makes each number&rsquo;s label in <code>ivr_dialing_info</code> on its <strong>home</strong> switch agree
        with who holds it here. Only labels that billing already recognises are written; rows on the other
        switch are shown as residue and never touched.
        {mode && <> Server mode: <strong>{mode.toLowerCase()}</strong>.</>}
      </p>
      <div className="space-y-3">
        <ParkedNote />
        {error && <ErrorLine text={error} />}

        {loading ? (
          <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Reading both switches…
          </div>
        ) : result ? (
          <ResultTable result={result} rolledBack={rolledBack} onRollback={doRollback} busy={busy} />
        ) : preview ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[12px]" style={{ color: 'var(--text-muted)' }}>
                  <th className="px-2 py-1.5 font-medium">Number</th>
                  <th className="px-2 py-1.5 font-medium">Home switch</th>
                  <th className="px-2 py-1.5 font-medium">Current label / flow</th>
                  <th className="px-2 py-1.5 font-medium">Holder</th>
                  <th className="px-2 py-1.5 font-medium">Action</th>
                  <th className="px-2 py-1.5 font-medium">New label</th>
                  <th className="px-2 py-1.5 font-medium">Why</th>
                </tr>
              </thead>
              <tbody>
                {preview.plans.map(p => (
                  <tr key={p.numberId} className="border-t align-top" style={{ borderColor: 'var(--border)' }}>
                    <td className="px-2 py-2 font-medium text-gray-900 dark:text-white">{p.e164}</td>
                    <td className="px-2 py-2 text-[12.5px]">{p.switchName || p.providerName || '—'}</td>
                    <td className="px-2 py-2 text-[12.5px]">
                      {describeRow(currentRow(p))}
                      {p.homeRows.length > 1 && (
                        <div className="text-[11.5px]" style={{ color: 'var(--danger-text)' }}>
                          {p.homeRows.length} rows: {p.homeRows.map(r => r.cliNumber).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-2 py-2 text-[12.5px]">
                      {p.holderName || p.holderOrganizationId
                        ? <>{p.holderName || p.holderOrganizationId}{p.holderBusinessId ? <span style={{ color: 'var(--text-muted)' }}> ({p.holderBusinessId})</span> : null}</>
                        : <span style={{ color: 'var(--text-muted)' }}>nobody</span>}
                    </td>
                    <td className="px-2 py-2"><span className={actionPill(p.action)}>{ACTION_TEXT[p.action]}</span></td>
                    <td className="px-2 py-2 text-[12.5px]">
                      {writes(p) ? `${p.newBusinessId}${p.newServiceId ? ` · flow ${p.newServiceId}` : ''}` : '—'}
                    </td>
                    <td className="px-2 py-2 text-[12px]" style={{ color: p.action === 'BLOCKED' ? 'var(--danger-text)' : 'var(--text-muted)' }}>
                      {p.reason}
                      {p.warnings.map(w => (
                        <div key={w} className="mt-0.5" style={{ color: 'var(--amber-label)' }}>⚠ {w}</div>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {!result && confirming && (
          <div className="text-[12.5px] px-3 py-2 rounded-lg"
               style={{ background: 'var(--amber-tint)', color: 'var(--amber-label)' }}>
            <strong>Write {n} row{n === 1 ? '' : 's'} on the live switch?</strong> Calls to these numbers are labelled
            (and billed) by the new value within about a minute. Each write is guarded on the row still holding
            what was previewed, backed up, and can be rolled back from the switch history.
          </div>
        )}
      </div>

      <div className="flex justify-end items-center gap-2 mt-4 flex-wrap">
        {!result && blockReason && !loading && (
          <span className="text-[12px] mr-auto" style={{ color: 'var(--text-muted)' }}>{blockReason}</span>
        )}
        {result ? (
          <button className="tl-btn tl-btn-primary" onClick={() => onClose(true)} disabled={busy}>Done</button>
        ) : confirming ? (
          <>
            <button className="tl-btn tl-btn-secondary" onClick={() => setConfirming(false)} disabled={busy}>Back</button>
            <button className="tl-btn tl-btn-danger" onClick={doApply} disabled={busy || !!blockReason}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Yes, write {n} change{n === 1 ? '' : 's'}
            </button>
          </>
        ) : (
          <>
            <button className="tl-btn tl-btn-secondary" onClick={() => onClose(false)} disabled={busy}>Cancel</button>
            <button className="tl-btn tl-btn-primary" onClick={() => setConfirming(true)}
                    disabled={busy || loading || !!blockReason}>
              <Send className="w-4 h-4" /> Apply {n} change{n === 1 ? '' : 's'}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

// ── Retire ─────────────────────────────────────────────────────────────────────

interface RetireOutcome { e164: string; ok: boolean; message: string; switchOutcome?: RetireSwitchOutcome }

const describeSwitchOutcome = (s?: RetireSwitchOutcome): string => {
  if (!s) return '';
  if (s.error) return `switch: ${s.error}`;
  if (s.applied) {
    const r = s.applied.results[0];
    return r ? `switch: ${ACTION_TEXT[r.action]} — ${r.status}${r.detail ? ` (${r.detail})` : ''}` : 'switch: nothing to do';
  }
  if (s.preview) {
    const p = s.preview.plans[0];
    return p ? `switch (preview only): would ${ACTION_TEXT[p.action].toLowerCase()} — ${p.reason}` : '';
  }
  return s.note ? `switch: ${s.note}` : '';
};

export const RetireModal: React.FC<{
  numbers: ProviderNumber[];
  onClose: (changed: boolean) => void;
}> = ({ numbers, onClose }) => {
  const [mode, setMode] = useState<SwitchPushMode | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcomes, setOutcomes] = useState<RetireOutcome[] | null>(null);

  useEffect(() => {
    let live = true;
    switchPushService.mode().then(r => { if (live && r.success && r.data) setMode(r.data.mode); });
    return () => { live = false; };
  }, []);

  const doRetire = async () => {
    setBusy(true);
    const out: RetireOutcome[] = [];
    // One at a time: each retire commits on its own and parks its own switch row, and a
    // failure on one number must not hide what happened to the others.
    for (const n of numbers) {
      if (typeof n.id !== 'number') continue;
      const r = await switchPushService.retire(n.id, reason.trim());
      out.push(r.success && r.data
        ? { e164: n.e164, ok: true, message: 'retired', switchOutcome: r.data.switch }
        : { e164: n.e164, ok: false, message: r.error || 'refused' });
    }
    setOutcomes(out);
    setBusy(false);
  };

  return (
    <Modal onClose={() => onClose(!!outcomes)} busy={busy}>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
        Retire {numbers.length === 1 ? numbers[0].e164 : `${numbers.length} numbers`}?
      </h2>
      {outcomes ? (
        <div className="space-y-1.5 mt-3">
          {outcomes.map(o => (
            <div key={o.e164} className="text-[12.5px]">
              <span className="font-medium text-gray-900 dark:text-white">{o.e164}</span>{' '}
              <span className={o.ok ? 'tl-pill tl-pill-success' : 'tl-pill tl-pill-coral'}>{o.ok ? 'retired' : 'failed'}</span>{' '}
              <span style={{ color: 'var(--text-muted)' }}>{o.ok ? describeSwitchOutcome(o.switchOutcome) : o.message}</span>
            </div>
          ))}
          <div className="flex justify-end mt-4">
            <button className="tl-btn tl-btn-primary" onClick={() => onClose(true)}>Done</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-[12.5px] mb-2" style={{ color: 'var(--text-muted)' }}>
            For garbage numbers that should never be handed out again. What happens:
          </p>
          <ul className="text-[12.5px] list-disc pl-5 space-y-1 mb-3" style={{ color: 'var(--text-muted)' }}>
            {retireConsequences(numbers, mode).map(c => <li key={c}>{c}</li>)}
          </ul>
          {mode === 'APPLY' && <ParkedNote />}
          <div className="mt-3">
            <label className="block text-[12px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Reason</label>
            <input className="tl-input" value={reason} onChange={e => setReason(e.target.value)}
                   placeholder="e.g. Dead line, returned to the carrier" />
            <p className="text-[12px] mt-1" style={{ color: 'var(--text-muted)' }}>
              Recorded on the closed tenure as &ldquo;retired: &hellip;&rdquo;.
            </p>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button className="tl-btn tl-btn-secondary" onClick={() => onClose(false)} disabled={busy}>Cancel</button>
            <button className="tl-btn tl-btn-danger" onClick={doRetire} disabled={busy || !reason.trim()}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
              Retire {numbers.length === 1 ? 'number' : `${numbers.length} numbers`}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

// ── Switch history (per number) ────────────────────────────────────────────────

export const SwitchHistoryPanel: React.FC<{ e164: string }> = ({ e164 }) => {
  const [rows, setRows] = useState<SwitchPushRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await switchPushService.history(e164);
    if (r.success && r.data) setRows(r.data.content || []);
    else setError(r.error || 'Could not load the switch history');
  }, [e164]);

  useEffect(() => { load(); }, [load]);

  const doRollback = async (id: number) => {
    setBusy(true);
    setError(null);
    const r = await switchPushService.rollback(id);
    setBusy(false);
    if (!r.success) setError(r.error || 'Rollback was refused');
    else if (r.data && r.data.status !== 'OK') setError(r.data.detail || 'Rollback failed');
    load();
  };

  return (
    <div>
      <div className="text-[12px] font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>
        Switch pushes for {e164}
      </div>
      {error && <ErrorLine text={error} />}
      {rows === null ? (
        <div className="flex items-center gap-2 text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
          Never pushed from cpass. Any label on the switch was set by hand or by the IVR sync.
        </div>
      ) : (
        <div className="space-y-1.5">
          {rows.map(r => (
            <div key={r.id} className="text-[12.5px] flex items-start gap-2 flex-wrap">
              <span className={actionPill(r.action)}>{ACTION_TEXT[r.action]}</span>
              <span className={r.status === 'OK' ? 'tl-pill tl-pill-success' : r.status === 'FAILED' ? 'tl-pill tl-pill-coral' : 'tl-pill'}>
                {r.status}
              </span>
              <span className="text-gray-900 dark:text-white">
                {(r.oldBusinessId || '∅')}/{(r.oldServiceId || '∅')} → {(r.newBusinessId || '∅')}/{(r.newServiceId || '∅')}
              </span>
              <span style={{ color: 'var(--text-muted)' }}>
                {formatWhen(r.createdAt)} by {r.requestedBy}
                {r.rollbackOf ? ` · undid #${r.rollbackOf}` : ''}
                {r.detail ? ` · ${r.detail}` : ''}
              </span>
              {canRollback(r, rows) && (
                <button className="tl-btn tl-btn-secondary tl-btn-sm" disabled={busy} onClick={() => doRollback(r.id)}>
                  <Undo2 className="w-3.5 h-3.5" /> Rollback
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
